import { api } from '@/lib/api'

/** Normaliza listagens DRF / hi Conecta (`dados` | `results` | array). */
export function unwrapList<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[]
  if (res && typeof res === 'object') {
    const o = res as Record<string, unknown>
    if (Array.isArray(o.dados)) return o.dados as T[]
    if (Array.isArray(o.results)) return o.results as T[]
  }
  return []
}

export function unwrapDados<T>(res: unknown): T | undefined {
  if (res && typeof res === 'object' && 'dados' in res) {
    return (res as { dados: T }).dados
  }
  return res as T
}

// ── Alertas ──────────────────────────────────────────────────

export type AlertaItem = {
  id: string
  titulo?: string
  tipo?: string
  tipo_display?: string
  prioridade?: string
  status?: string
  mensagem?: string
  descricao?: string
  created_at?: string
  [key: string]: unknown
}

export async function fetchAlertas(params: { status?: string; prioridade?: string } = {}) {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.prioridade) qs.set('prioridade', params.prioridade)
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<{ sucesso?: boolean; dados: AlertaItem[]; nao_lidos?: number }>(
    `/analytics/alertas/${suffix}`,
  )
}

export async function resolverAlerta(id: string, acao: 'resolver' | 'ignorar' | 'ler' = 'resolver') {
  return api.post<{ sucesso?: boolean; status?: string }>(`/analytics/alertas/${id}/acao/`, {
    acao,
  })
}

// ── Documentos ───────────────────────────────────────────────

export type DocumentoItem = {
  id: string
  titulo?: string
  status?: string
  status_display?: string
  tipo_nome?: string
  tipo?: { nome?: string } | string
  colaborador?: string
  colaborador_nome?: string
  created_at?: string
  [key: string]: unknown
}

export async function fetchMeusDocumentos(opts: { colaboradorId?: string; origem?: string } = {}) {
  const qs = new URLSearchParams()
  if (opts.colaboradorId) qs.set('colaborador_id', opts.colaboradorId)
  if (opts.origem) qs.set('origem', opts.origem)
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<{
    sucesso?: boolean
    dados: DocumentoItem[]
    pendentes?: number
    colaborador_id?: string
  }>(`/documentos/meus/${suffix}`)
}

export async function fetchGedDocumentos(opts: {
  busca?: string
  status?: string
  colaboradorId?: string
  limit?: number
} = {}) {
  const qs = new URLSearchParams()
  if (opts.busca) qs.set('busca', opts.busca)
  if (opts.status) qs.set('status', opts.status)
  if (opts.colaboradorId) qs.set('colaborador_id', opts.colaboradorId)
  if (opts.limit) qs.set('limit', String(opts.limit))
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<{
    sucesso?: boolean
    dados: DocumentoItem[]
    pendentes?: number
  }>(`/documentos/ged/${suffix}`)
}

export async function fetchTiposDocumento() {
  const res = await api.get<{ sucesso?: boolean; dados: Array<{ id: string; nome: string }> }>(
    '/documentos/tipos/',
  )
  return res.dados || []
}

export async function assinarDocumento(id: string) {
  return api.post<{ sucesso?: boolean; dados?: DocumentoItem; mensagem?: string }>(
    `/documentos/meus/${id}/assinar/`,
    {},
  )
}

// ── Reuniões ─────────────────────────────────────────────────

export type ReuniaoItem = {
  id: string
  titulo?: string
  status?: string
  tipo?: string
  data?: string
  data_hora?: string
  gestor_nome?: string
  [key: string]: unknown
}

export async function fetchReunioes() {
  const res = await api.get<unknown>('/flow-intelligence/reunioes/')
  return unwrapList<ReuniaoItem>(res)
}

export async function fetchReunioesDashboard() {
  return api.get<Record<string, unknown>>('/flow-intelligence/reunioes/dashboard/')
}

export async function marcarReuniaoRealizada(id: string) {
  return api.post(`/flow-intelligence/reunioes/${id}/marcar-realizada/`, { ata: '' })
}

// ── Indicadores ──────────────────────────────────────────────

export async function fetchIndicadoresRh() {
  const res = await api.get<{ sucesso?: boolean; dados: Record<string, unknown> }>(
    '/rh/indicadores/',
  )
  return res.dados || {}
}

export async function fetchAnalyticsDashboard() {
  const res = await api.get<{ sucesso?: boolean; dados: Record<string, unknown> }>(
    '/analytics/dashboard/',
  )
  return res.dados || {}
}

export async function fetchRhKpis() {
  const res = await api.get<{ sucesso?: boolean; dados: Record<string, number> }>(
    '/rh/colaboradores/kpis/',
  )
  return res.dados || {}
}

// ── Knowledge / Onboarding ───────────────────────────────────

export async function fetchKnowledge() {
  const res = await api.get<unknown>('/flow-intelligence/knowledge/')
  return unwrapList<Record<string, unknown>>(res)
}

export async function fetchOnboardingMaps() {
  const res = await api.get<unknown>('/flow-intelligence/onboarding/')
  return unwrapList<Record<string, unknown>>(res)
}

// ── IA RH ────────────────────────────────────────────────────

export async function consultarIa(pergunta: string, categoria = 'geral') {
  return api.post<{ sucesso?: boolean; dados?: Record<string, unknown>; mensagem?: string }>(
    '/inteligencia/ia/consultar/',
    { pergunta, categoria },
  )
}

export async function fetchIaHistorico() {
  const res = await api.get<unknown>('/inteligencia/ia/historico/')
  return unwrapList<Record<string, unknown>>(res)
}

// ── Integrações ──────────────────────────────────────────────

export async function fetchRhsolutioHealth() {
  return api.post<Record<string, unknown>>('/integracoes/rhsolutio/health/')
}

export async function fetchRhsolutioPendencias() {
  return api.get<{
    sucesso?: boolean
    pendentes?: number
    itens?: unknown[]
    mensagem?: string
  }>('/integracoes/rhsolutio/pendencias/')
}

export async function fetchBitrixColaboradores() {
  return api.get<unknown>('/integracoes/bitrix/colaboradores/')
}

// ── Workflows health ─────────────────────────────────────────

export async function fetchWorkflowsHealth() {
  return api.get<Record<string, unknown>>('/workflows/health/')
}
